import axios, { AxiosResponse } from 'axios';
import type { ApiResponse } from '@/types/api.ts';

// ==================== TYPE DEFINITIONS ====================

export interface LoginCredentials {
    clientUsername?: string;
    username: string;
    password: string;
}

export interface LoginResponse {
    success: boolean;
    user?: {
        _id: string;
        name: string;
        email: string;
        groupId?: string;
    };
    message?: string;
}

export interface AuthResponse {
    authenticated: boolean;
    user?: {
        _id: string;
        name: string;
        email: string;
        groupId?: string;
    };
}

// ==================== AUTH API CLASS ====================

export class AuthAPI {
    /**
     * Login user with username and password
     */
    async login(credentials: LoginCredentials): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/login', credentials);
            return {
                success: true
            };
        } catch (error: any) {
            // Extract error message from response
            let errorMessage = 'Greška pri prijavljivanju';
            
            if (error.response?.data) {
                // If response data is a string, use it directly
                errorMessage = typeof error.response.data === 'string' 
                    ? error.response.data 
                    : error.response.data.message || error.response.data.error || errorMessage;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            return {
                success: false,
                error: errorMessage
            };
        }
    }

    /**
     * Logout current user
     */
    async logout(): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/logout');
            return {
                success: true
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Logout failed'
            };
        }
    }

    /**
     * Check if user is authenticated
     */
    async checkAuthentication(): Promise<ApiResponse<AuthResponse>> {
        try {
            const response: AxiosResponse<AuthResponse> = await axios.get('/api/authenticated');
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Authentication check failed'
            };
        }
    }

    /**
     * Get current authenticated user
     */
    async getCurrentUser(): Promise<ApiResponse<any>> {
        try {
            const response: AxiosResponse<any> = await axios.get('/api/user/get');
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch current user'
            };
        }
    }
}

// ==================== EXPORTS ====================

// Create singleton instance
export const authApi = new AuthAPI();

// Default export for convenience
export default authApi;